import {
  loadStripe,
  type PaymentRequestPaymentMethodEvent,
  type Stripe,
  type StripeElements,
  type StripePaymentElement,
} from "@stripe/stripe-js";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { signupQueries } from "../queries";
import stripeAppearance from "~/components/Payment/stripeAppearance";

export type PaymentMethod = "card" | "ach" | "google-pay" | "apple-pay";

export interface PaymentMemberData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
}

export const useStripeCheckout = (checkoutParams: {
  onSuccess: (data: {
    userData: PaymentMemberData;
    setupIntentId: string;
  }) => void;
  onError: (errorMessage: string) => void;
  ach?: MaybeRefOrGetter<boolean>;
  selectedPaymentMethod?: MaybeRefOrGetter<PaymentMethod | undefined>;
}) => {
  const config = useRuntimeConfig();
  const queryClient = useQueryClient();

  // State
  const hasInited = ref(false);
  const hasRenderedWallet = ref(false);
  const hasRenderedCard = ref(false);
  const isConfirming = ref(false);
  const walletPaymentMethods = reactive({
    applePay: false,
    googlePay: false,
  });

  // Stripe Elements
  const stripe = ref<Stripe | null>(null);
  const stripeElements = ref<StripeElements | null>(null);
  const paymentElement = ref<StripePaymentElement | null>(null);

  const params = computed(() => {
    if (toValue(checkoutParams.selectedPaymentMethod) === "ach") {
      return {
        paymentMethod: "us_bank_account",
      } as const;
    }
    return {
      paymentMethod: "card",
    } as const;
  });

  const { data, refetch } = useQuery({
    ...signupQueries.setupStripe(params),
    enabled: false,
  });

  const paymentMethods = computed(() => {
    const items = [
      {
        name: "Credit Card",
        value: "card",
        icon: "/images/icons/credit-card.png",
      },
    ];
    if (toValue(checkoutParams.ach)) {
      items.unshift({
        name: "ACH",
        value: "ach",
        icon: "/images/icons/bank-account.png",
      });
    }

    if (walletPaymentMethods.googlePay) {
      items.unshift({
        name: "Google Pay",
        value: "google-pay",
        icon: "/images/icons/google-pay.png",
      });
    }

    if (walletPaymentMethods.applePay) {
      items.unshift({
        name: "Apple Pay",
        value: "apple-pay",
        icon: "/images/icons/apple-pay.png",
      });
    }

    return items;
  });

  onUnmounted(() => {
    // Invalidate cache
    queryClient.invalidateQueries({ queryKey: ["setupPayment"] });
  });

  const renderCardElements = async (params: { containerElementId: string }) => {
    hasRenderedCard.value = false;

    // Load Stripe and refetch the setup intent
    const [stripeInstance] = await Promise.all([
      loadStripe(config.public.stripePublicKey),
      refetch(),
    ]);

    stripe.value = stripeInstance;

    if (!stripe.value) {
      throw new Error("Stripe is not loaded.");
    }

    // Create the Stripe Elements
    stripeElements.value = stripe.value.elements({
      clientSecret: data.value!.clientSecret,
      appearance: stripeAppearance,
    });

    paymentElement.value = stripeElements.value.create("payment", {
      fields: {
        billingDetails: {
          address: {
            postalCode: "never",
          },
        },
      },
      wallets: {
        googlePay: "never",
        applePay: "never",
      },
    });

    // Wait for the payment element to be ready
    paymentElement.value.on("ready", () => {
      hasRenderedCard.value = true;
    });

    const element = document.getElementById(params.containerElementId);

    if (!element) {
      throw new Error("Element not found.");
    }

    paymentElement.value.mount(element);
  };

  const renderWalletElements = async (params: {
    amount: number;
    containerElementId: string;
  }) => {
    // If the wallet elements have already been rendered, do nothing
    if (hasRenderedWallet.value) {
      return;
    }
    if (!stripe.value) {
      throw new Error("Stripe is not loaded.");
    }

    const amount = Math.round(params.amount * 100);

    const paymentRequest = stripe.value.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Dollar Donation Club",
        amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    const result = await paymentRequest.canMakePayment();

    if (result) {
      const elements = stripe.value.elements();
      const prButton = elements.create("paymentRequestButton", {
        paymentRequest,
      });

      paymentRequest.on("paymentmethod", (ev) => {
        confirmStripeWallet({
          ev,
          clientSecret: data.value!.clientSecret,
        });
      });

      prButton.on("ready", () => {
        hasRenderedWallet.value = true;
      });

      const element = document.getElementById(params.containerElementId);

      if (!element) {
        throw new Error("Wallet Container Element not found.");
      }

      prButton.mount(element);
    }
  };

  const init = async (params: {
    totalAmount: number;
    cardContainerId: string;
    walletContainerId: string;
  }) => {
    const stripeInstance = await loadStripe(config.public.stripePublicKey);

    stripe.value = stripeInstance;

    if (!stripe.value) {
      throw new Error("Stripe is not loaded.");
    }

    const paymentRequest = stripe.value.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Dollar Donation Club",
        amount: Math.round(params.totalAmount * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const result = await paymentRequest.canMakePayment();

    if (result) {
      walletPaymentMethods.applePay = result.applePay;
      walletPaymentMethods.googlePay = result.googlePay;
    }

    renderCardElements({
      containerElementId: params.cardContainerId,
    });

    if (walletPaymentMethods.applePay || walletPaymentMethods.googlePay) {
      renderWalletElements({
        amount: params.totalAmount,
        containerElementId: params.walletContainerId,
      });
    }

    hasInited.value = true;
  };

  const confirmCardPayment = async (params: PaymentMemberData) => {
    if (!stripe.value || !stripeElements.value) {
      throw new Error("Stripe is not loaded.");
    }

    isConfirming.value = true;
    const result = await stripe.value?.confirmSetup({
      elements: stripeElements.value as StripeElements,
      redirect: "if_required",
      confirmParams: {
        payment_method_data: {
          billing_details: {
            address: {
              postal_code: params.postalCode,
            },
          },
        },
      },
    });

    if (result?.error) {
      console.error(result.error);
      checkoutParams.onError(
        result.error.message ??
          "Oh snap! The payment was declined. Select another payment method and try again.",
      );
      isConfirming.value = false;

      return;
    }

    isConfirming.value = false;
    checkoutParams.onSuccess({
      userData: {
        ...params,
      },
      setupIntentId: result!.setupIntent!.id,
    });
  };

  const confirmStripeWallet = async (params: {
    ev: PaymentRequestPaymentMethodEvent;
    clientSecret: string;
  }) => {
    if (!stripe.value) {
      throw new Error("Stripe is not loaded.");
    }

    const confirmResult = await stripe.value!.confirmCardSetup(
      params.clientSecret,
      { payment_method: params.ev.paymentMethod.id },
      { handleActions: false },
    );

    if (confirmResult.error) {
      // Report to the browser that the payment failed, prompting it to
      // re-show the payment interface, or show an error message and close
      // the payment interface.
      params.ev.complete("fail");
    } else {
      // Report to the browser that the confirmation was successful, prompting
      // it to close the browser payment method collection interface.
      params.ev.complete("success");

      const intentObject = confirmResult.setupIntent;

      // Get the user data
      const nameParts = params.ev.payerName!.split(" ");
      const userData = {
        email: params.ev.payerEmail || "",
        phone: params.ev.payerPhone || "",
        firstName: nameParts[0] || "",
        lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",
        city: params.ev.paymentMethod?.billing_details?.address?.city || "",
        state: params.ev.paymentMethod?.billing_details?.address?.state || "",
        postalCode:
          params.ev.paymentMethod?.billing_details?.address?.postal_code || "",
        country:
          params.ev.paymentMethod?.billing_details?.address?.country || "",
      };

      if (intentObject.status === "requires_action") {
        // Let Stripe.js handle the rest of the payment flow.
        const result = await stripe.value.confirmCardSetup(params.clientSecret);

        if (result.error) {
          // The payment failed -- ask your customer for a new payment method.
          checkoutParams.onError(
            result.error.message ??
              "Payment failed. Please try a new payment method.",
          );
        } else {
          // The payment has succeeded.
          const data = {
            setupIntentId: result.setupIntent.id,
            userData,
          };

          checkoutParams.onSuccess(data);
        }
      } else {
        // The payment has succeeded.
        const data = {
          setupIntentId: confirmResult.setupIntent.id,
          userData,
        };

        checkoutParams.onSuccess(data);
      }
    }
  };

  return {
    data,
    paymentMethods,
    hasInited,
    isConfirming,
    walletPaymentMethods,
    init,
    renderCardElements,
    renderWalletElements,
    confirmCardPayment,
    hasRenderedCard,
  };
};
